import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom, Subscription } from 'rxjs';
import { DateTime } from 'luxon';

import { EventService } from '../core/services/event.service';
import { AnalyticsService } from '../core/services/analytics.service';
import { ApiCacheService } from '../core/services/api-cache.service';
import { InviteCodeStore } from '../core/services/invite-code-store.service';
import { AdsConversionService } from '../core/services/ads-conversion.service';
import { GuestHostPromptService } from '../core/services/guest-host-prompt.service';
import { CoverImageComponent } from '../cover-image/cover-image.component';
import { INVITE_HASH_EVENT_KEY } from '../invite-share/invite-share.component';
import { CreateEventFeaturesService } from '../core/services/create-event-features.service';
import { WhatsappUploadService } from '../core/services/whatsapp-upload.service';
import { buildTimeOptions, durationLabel, endAfterStartWithinTenDays, endLabel, floorToSlot, slotDateTime, slotTo24h } from './event-time';
import {
  isPopularCategory, loadRecentCategories, namePlaceholderFor, POPULAR_CATEGORIES, rememberCategory, uniqueCategories,
} from './categories';
import { CategorySource } from './category-sheet/category-sheet.component';
import { StartOption, StartSheetComponent } from './start-sheet/start-sheet.component';
import { DurationSheetComponent } from './duration-sheet/duration-sheet.component';
import { findRecentDuplicate, RecentCreate, rememberCreate } from './recent-creates';
import { VoiceParsed, VoicePanelComponent } from './voice/voice-panel.component';
import { FieldKey, FIELD_NAMES, FilledBy, freshFilledBy, mergeParse, missedFields, VoicePatch } from './voice/voice-merge';
import { ParseResult } from './voice/event-details-parser';
import { ChatFormState, ChatHost, ChatStep } from './voice/chat-script';
import { VoiceChatComponent } from './voice/voice-chat.component';

/** Where the form is being shown — tags every analytics event. */
export type CreateEventSurface = 'quick_create' | 'sheet';

/** What a successful create hands back to the surface that hosts the form. */
export interface CreatedEvent {
  eventId: any;
  viewerHex: string;
  eventName: string;
  category: string;
  hostedBy: string;
  /** "Sat 27 Sep, 12:00 AM", the end. The invite card shows `start` instead. */
  endSummary: string;
  /** The cover as picked (a data URL), or null. */
  coverImage: string | null;
  start: Date;
  end: Date;
}

/** A restorable copy of the form, for surfaces that must survive a reload (sign-in redirects). */
export interface CreateEventDraft {
  eventCategory: string;
  eventName: string;
  hostedBy: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  durationHours: number | null;
  showCustomCategory: boolean;
  coverImage: string | null;
  default_image_id: string | null;
  /** Where each answer came from (rows layout + voice); absent on older drafts. */
  filledBy?: FilledBy;
}

/**
 * The one create-event form, used by every surface that creates an event:
 * the new-user page (/auth/start) and the in-app "Create Event" sheet. Fields,
 * defaults, wording, validation, the cover picker, the create call, error
 * handling and analytics all live here, so the surfaces cannot drift apart
 * again — change it once and it changes everywhere.
 *
 * The host surface supplies only what is genuinely its own: the heading and
 * chrome around the form, a pre-filled host name when there is an account,
 * the footnote under Create, and — on the new-user page — a `beforeCreate`
 * hook that asks the visitor to sign in first. After a successful create it
 * emits `created`; the surface then opens the invite screen.
 *
 * Styling is the design system on the global tokens (styles.css): ink chips
 * and focus, one hero red on Create.
 */
@Component({
  selector: 'app-create-event-form',
  templateUrl: './create-event-form.component.html',
  styleUrls: ['./create-event-form.component.scss'],
})
export class CreateEventFormComponent implements OnInit, OnDestroy, ChatHost {

  @Input() surface: CreateEventSurface = 'sheet';
  /** Pre-fills "Hosted by" — the account name in-app; empty for a visitor with no account yet. */
  @Input() initialHostedBy = '';
  /** Small print under Create. */
  @Input() footnote = '';
  /**
   * Runs after the form validates and before the event is created. Resolve
   * false to stop — the new-user page uses this to ask for sign-in, then calls
   * create() itself once the visitor is signed in.
   */
  @Input() beforeCreate?: () => Promise<boolean>;
  /** Offer "Use a different account instead" when the account already has a live event. */
  @Input() allowSwitchAccount = false;
  /** Answers to put back on load — the new-user page's draft, saved before a sign-in redirect. */
  @Input() initialDraft: Partial<CreateEventDraft> | null = null;

  @Output() created = new EventEmitter<CreatedEvent>();
  /** True while the create call is in flight — for a surface that shows its own overlay. */
  @Output() workingChange = new EventEmitter<boolean>();
  @Output() switchAccount = new EventEmitter<void>();
  /**
   * "Edit it instead" on the you-already-have-this prompt, with the existing
   * event's hex. The surface closes itself and opens the editor
   * (EventEditLauncherService); the form has no way to do either.
   */
  @Output() editExisting = new EventEmitter<string>();

  /** A recent event of theirs that looks like this one (recent-creates.ts). Shown in place of Create. */
  duplicateOf: RecentCreate | null = null;
  /** "Create a new one" was chosen for this event id; don't ask about it again. */
  private duplicateDismissedFor: string | null = null;

  readonly categoryPills: string[] = ['Wedding', 'Birthday', 'Holiday'];
  readonly OTHER = 'Other';
  readonly timeOptions: string[] = buildTimeOptions();

  /**
   * Duration presets. Hosts think "it's an evening thing" or "it runs all
   * weekend", not "ends Thursday 9:00 PM", so the end is computed from the
   * start; "Custom" falls back to explicit end fields.
   */
  readonly durations: { label: string; hours: number }[] = [
    { label: '4 hours', hours: 4 },
    { label: '6 hours', hours: 6 },
    { label: '8 hours', hours: 8 },
    { label: '1 day', hours: 24 },
    { label: '3 days', hours: 72 },
  ];
  /** Default 4 hours — most events are an evening, and an end is easier to extend than to notice and shorten. */
  static readonly DEFAULT_DURATION_HOURS = 4;
  /** Selected preset in hours; null while "Custom" is active. */
  durationHours: number | null = CreateEventFormComponent.DEFAULT_DURATION_HOURS;

  showCustomCategory = false;
  showCustomEnd = false;
  coverImage: string | null = null;
  default_image_id: string | null = null;
  /** Where the cover came from, for analytics. */
  private coverType: 'uploaded' | 'stock' | null = null;
  /** The start the form was seeded with — to tell "starts now" from a changed start. */
  private seededStart = '';

  isWorking = false;
  errorMessage = '';
  /** The account already has a live event — offer a way out instead of a dead-end retry. */
  liveEventConflict = false;

  // ── v2: rows + sheets + voice (docs/CREATE_EVENT_VOICE_PLAN.md) ────────
  /** Grouped rows with bottom sheets, instead of chips. Decided once, on init. */
  rows = false;
  /** "Say it instead". Only ever true with `rows`. */
  voice = false;
  voiceMaxSeconds = 30;
  openSheet: 'category' | 'start' | 'duration' | null = null;
  /** The host's own categories: past events plus ones added on this device. */
  yourCategories: string[] = [];
  private categoriesRequested = false;
  /** Where each answer came from. Voice never overwrites a 'user' field. */
  filledBy: FilledBy = freshFilledBy();
  voiceNotes: string[] = [];
  voicePartial = '';
  /** The host edited the form after a voice fill: the "We heard" card shrinks to a line. */
  voiceCollapsed = false;
  private usedVoice = false;
  /** The form as it was before the last voice fill, for Undo. */
  private voiceSnapshot: { draft: CreateEventDraft; filledBy: FilledBy } | null = null;

  // ── "Plan it with me" (voice/voice-chat.component.ts) ──────────────────
  /** The conversation instead of "Say it instead". Only ever true with `voice`. */
  chatEnabled = false;
  /** The conversation is running: its panel is up and Create lives in it. */
  chatActive = false;
  /** The field the conversation is asking about — ringed in the template. */
  chatStep: ChatStep | null = null;
  /** The tile's resting waveform: bar heights in %, decoration only (same as "Say it instead"). */
  readonly chatWave = [30, 45, 35, 25, 20, 20, 25, 20, 20, 25, 35, 45, 40, 30, 25, 20, 25, 20, 20, 30, 45, 55, 40, 30,
    25, 20, 20, 25, 20, 25, 35, 30, 25, 20, 25, 30, 45, 60, 45, 70];
  /** Fields just filled from the conversation, for a brief highlight. */
  justFilled = new Set<string>();
  private justFilledTimer: any = null;
  private usedChat = false;
  /** Resolves the conversation's wait on a picker sheet it opened. */
  private sheetWaiter: (() => void) | null = null;

  @ViewChild(VoiceChatComponent) voiceChat?: VoiceChatComponent;
  @ViewChild(VoicePanelComponent) voicePanel?: VoicePanelComponent;
  @ViewChild(DurationSheetComponent) durationSheet?: DurationSheetComponent;
  @ViewChild(StartSheetComponent) startSheet?: StartSheetComponent;
  /** The Runs for sheet shows Done only while its custom end is open. */
  durationCustomOpen = false;

  form = new FormGroup({
    eventCategory: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    eventName: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    hostedBy: new FormControl('', [Validators.maxLength(50)]),
    startDate: new FormControl('', [Validators.required]),
    startTime: new FormControl('', [Validators.required]),
    endDate: new FormControl('', [Validators.required]),
    endTime: new FormControl('', [Validators.required]),
  }, { validators: [endAfterStartWithinTenDays] });

  private subs = new Subscription();

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private eventService: EventService,
    private analytics: AnalyticsService,
    private apiCache: ApiCacheService,
    private inviteCodes: InviteCodeStore,
    private adsConversion: AdsConversionService,
    private guestHostPrompt: GuestHostPromptService,
    private features: CreateEventFeaturesService,
    private whatsappUpload: WhatsappUploadService,
    private el: ElementRef<HTMLElement>,
    private afAuth: AngularFireAuth,
  ) { }

  ngOnInit(): void {
    this.rows = this.features.rowsEnabled();
    this.voice = this.features.voiceEnabled();
    this.chatEnabled = this.voice && this.features.voiceChatEnabled();
    this.voiceMaxSeconds = this.features.voiceMaxSeconds;
    this.seedTiming();
    if (this.initialDraft) {
      this.restore(this.initialDraft);
    } else if (this.initialHostedBy) {
      this.form.patchValue({ hostedBy: this.initialHostedBy }, { emitEvent: false });
    }

    // A preset stays live: moving the start drags the end with it.
    this.subs.add(this.form.get('startDate')!.valueChanges.subscribe(() => this.applyDuration()));
    this.subs.add(this.form.get('startTime')!.valueChanges.subscribe(() => this.applyDuration()));

    // Typing in a text field is the host's own answer. Voice and restore()
    // patch with emitEvent: false, so only real edits land here.
    this.subs.add(this.form.get('eventName')!.valueChanges.subscribe(() => this.markUser('eventName')));
    this.subs.add(this.form.get('hostedBy')!.valueChanges.subscribe(() => this.markUser('hostedBy')));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    clearTimeout(this.justFilledTimer);
    this.resolveSheetWaiter();
  }

  // ── drafts (for surfaces that must survive a reload) ──────────────────

  snapshot(): CreateEventDraft {
    const v = this.form.getRawValue();
    return {
      eventCategory: v.eventCategory || '',
      eventName: v.eventName || '',
      hostedBy: v.hostedBy || '',
      startDate: v.startDate || '',
      startTime: v.startTime || '',
      endDate: v.endDate || '',
      endTime: v.endTime || '',
      durationHours: this.durationHours,
      showCustomCategory: this.showCustomCategory,
      coverImage: this.coverImage,
      default_image_id: this.default_image_id,
      filledBy: { ...this.filledBy },
    };
  }

  restore(draft: Partial<CreateEventDraft>): void {
    this.form.patchValue({
      eventCategory: draft.eventCategory || '',
      eventName: draft.eventName || '',
      hostedBy: draft.hostedBy || '',
      startDate: draft.startDate || this.form.value.startDate || '',
      startTime: draft.startTime || this.form.value.startTime || '',
      endDate: draft.endDate || this.form.value.endDate || '',
      endTime: draft.endTime || this.form.value.endTime || '',
    }, { emitEvent: false });
    this.durationHours = draft.durationHours ?? null;
    this.showCustomEnd = this.durationHours === null;
    this.showCustomCategory = !!draft.showCustomCategory;
    this.coverImage = draft.coverImage ?? null;
    this.default_image_id = draft.default_image_id ?? null;
    this.filledBy = draft.filledBy ? { ...freshFilledBy(), ...draft.filledBy } : freshFilledBy();
  }

  // ── timing ────────────────────────────────────────────────────────────

  /**
   * Pre-answered rather than blank: starts now (floored to the current
   * 15-minute slot, so "now" is never future-dated) and runs for the default
   * preset. The preset is applied here, on load — the end used to be seeded
   * at +24h while the chip showed a shorter duration, so a host who accepted
   * the defaults got a day-long event.
   */
  private seedTiming(): void {
    const now = DateTime.now();
    this.form.patchValue({
      startDate: now.toFormat('yyyy-MM-dd'),
      startTime: floorToSlot(now),
    }, { emitEvent: false });
    this.seededStart = `${this.form.value.startDate} ${this.form.value.startTime}`;
    this.applyDuration();
  }

  selectDuration(hours: number): void {
    this.durationHours = hours;
    this.showCustomEnd = false;
    this.applyDuration();
    this.analytics.capture('create_event_duration_selected', { hours, surface: this.surface });
  }

  selectCustomDuration(): void {
    this.durationHours = null;
    this.showCustomEnd = true;
    this.analytics.capture('create_event_duration_selected', { hours: 'custom', surface: this.surface });
  }

  /** Recompute the end from start + preset. No-op while "Custom" is active. */
  private applyDuration(): void {
    if (this.durationHours === null) return;

    const v = this.form.value;
    const time24 = slotTo24h(v.startTime || '');
    if (!v.startDate || !time24) return;

    const start = DateTime.fromISO(`${v.startDate}T${time24}`);
    if (!start.isValid) return;

    const end = start.plus({ hours: this.durationHours });
    this.form.patchValue({
      endDate: end.toFormat('yyyy-MM-dd'),
      endTime: floorToSlot(end),
    }, { emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  /** "Sat 27 Sep, 12:00 AM" — reassurance without another control. */
  get endSummary(): string {
    const v = this.form.value;
    const time24 = slotTo24h(v.endTime || '');
    if (!v.endDate || !time24) return '';
    const end = DateTime.fromISO(`${v.endDate}T${time24}`);
    return end.isValid ? end.toFormat('ccc d LLL, h:mm a') : '';
  }

  // ── category ──────────────────────────────────────────────────────────

  get allPills(): string[] {
    return [...this.categoryPills, this.OTHER];
  }

  isPillSelected(pill: string): boolean {
    if (pill === this.OTHER) return this.showCustomCategory;
    return !this.showCustomCategory && this.form.value.eventCategory === pill;
  }

  selectPill(pill: string): void {
    if (pill === this.OTHER) {
      this.showCustomCategory = true;
      this.form.patchValue({ eventCategory: '' });
    } else {
      this.showCustomCategory = false;
      this.form.patchValue({ eventCategory: pill });
    }
    this.form.get('eventCategory')?.markAsTouched();
    this.analytics.capture('create_event_category_selected', {
      category: pill,
      is_custom: pill === this.OTHER,
      surface: this.surface,
    });
  }

  // ── rows layout (v2) ──────────────────────────────────────────────────

  /** The name placeholder follows the category: Birthday → "Anaya's 5th Birthday". */
  get namePlaceholder(): string {
    return namePlaceholderFor(this.form.value.eventCategory || '');
  }

  /** "Now · Tue 29 Sep, 8:15 PM" while it still starts now, else "Sat 3 Oct, 3:00 PM". */
  get startSummary(): string {
    const v = this.form.value;
    const start = slotDateTime(v.startDate || '', v.startTime || '');
    if (!start) return '';
    // Non-breaking spaces: a narrow row wraps at the dot, never inside "3:00 PM".
    const label = start.toFormat('ccc d LLL, h:mm\u00a0a');
    return `${v.startDate} ${v.startTime}` === this.seededStart ? `Now · ${label}` : label;
  }

  /** "4 hours · ends 12:15 AM" — the duration and where it lands. */
  get runsForSummary(): string {
    const v = this.form.value;
    const start = slotDateTime(v.startDate || '', v.startTime || '');
    const end = slotDateTime(v.endDate || '', v.endTime || '');
    if (!start || !end || end <= start) return '';
    const hours = this.durationHours ?? end.diff(start, 'hours').hours;
    return `${durationLabel(hours)} · ends\u00a0${endLabel(start, end).replace(/ (AM|PM)$/, '\u00a0$1')}`;
  }

  get categoryInvalid(): boolean {
    const c = this.form.get('eventCategory');
    return !!c && c.touched && c.invalid;
  }

  /** Popular labels plus the host's own — what the parser may match. */
  get knownCategories(): string[] {
    return uniqueCategories([...POPULAR_CATEGORIES.map(c => c.label), ...this.yourCategories]);
  }

  openSheetFor(sheet: 'category' | 'start' | 'duration'): void {
    if (sheet === 'category') this.loadYourCategories();
    if (sheet === 'duration') this.durationCustomOpen = this.durationHours === null;
    this.openSheet = sheet;
    this.analytics.capture('create_event_sheet_opened', { sheet, surface: this.surface });
  }

  closeSheet(): void {
    if (this.openSheet === 'category') this.form.get('eventCategory')?.markAsTouched();
    this.finishSheet();
  }

  onCategoryPicked(e: { category: string; source: CategorySource }): void {
    const popular = isPopularCategory(e.category);
    this.form.patchValue({ eventCategory: e.category });
    this.form.get('eventCategory')?.markAsTouched();
    this.showCustomCategory = !popular;
    this.markUser('eventCategory');
    if (e.source === 'added') {
      rememberCategory(e.category);
      this.yourCategories = uniqueCategories([e.category, ...this.yourCategories]);
      this.analytics.capture('create_event_category_added', { length: e.category.length, surface: this.surface });
    }
    // A host's own category name stays out of analytics; popular labels are safe to send.
    this.analytics.capture('create_event_category_selected', {
      category: popular ? e.category : 'custom',
      is_custom: !popular,
      source: e.source,
      surface: this.surface,
    });
    this.finishSheet();
  }

  onStartPicked(e: { date: string; time: string; option: StartOption }): void {
    // Not emitEvent: false — the start subscription drags the end along.
    this.form.patchValue({ startDate: e.date, startTime: e.time });
    this.markUser('start');
    this.analytics.capture('create_event_start_selected', { option: e.option, surface: this.surface });
    this.finishSheet();
  }

  onDurationPreset(hours: number): void {
    this.selectDuration(hours);
    this.markUser('duration');
    this.finishSheet();
  }

  onDurationCustom(e: { endDate: string; endTime: string }): void {
    this.selectCustomDuration();
    this.form.patchValue({ endDate: e.endDate, endTime: e.endTime });
    this.form.updateValueAndValidity();
    this.markUser('duration');
    this.finishSheet();
  }

  /** Every way a picker sheet closes — picked or dismissed — goes through here. */
  private finishSheet(): void {
    this.openSheet = null;
    this.resolveSheetWaiter();
  }

  private resolveSheetWaiter(): void {
    const resolve = this.sheetWaiter;
    this.sheetWaiter = null;
    resolve?.();
  }

  /** The Starts sheet's Done. */
  onStartDone(): void {
    this.startSheet?.confirm();
  }

  /** The Runs for sheet's Done: only shown while "Pick an end time" is open. */
  onDurationDone(): void {
    this.durationSheet?.confirmCustom();
  }

  /**
   * The host's own categories, for the sheet's "Yours" and for voice matching:
   * ones added on this device, plus past events' categories from the API when
   * signed in. Fetched once per form, only when first needed.
   */
  loadYourCategories(): void {
    if (this.categoriesRequested) return;
    this.categoriesRequested = true;
    this.yourCategories = uniqueCategories([...loadRecentCategories(), ...this.yourCategories]);
    // Before sign-in (the new-user page) there is no account to ask.
    if (!localStorage.getItem('user')) return;
    this.eventService.getAllCategories()
      .then(obs => obs.subscribe({
        next: (res: any) => {
          if (Array.isArray(res)) {
            this.yourCategories = uniqueCategories([...this.yourCategories, ...res.filter(x => typeof x === 'string')]);
          }
        },
        error: () => { /* "Yours" just stays local — nothing to tell the host */ },
      }))
      .catch(() => { /* token refresh failed — same */ });
  }

  // ── voice (v2) ────────────────────────────────────────────────────────

  isVoiceFilled(field: FieldKey): boolean {
    return this.filledBy[field] === 'voice';
  }

  /**
   * The host answered a field themselves. Voice will leave it alone from now
   * on, a voice-filled field being corrected is worth knowing, and the "We
   * heard" card shrinks so it doesn't sit above a form that no longer matches.
   */
  markUser(field: FieldKey): void {
    if (this.filledBy[field] === 'user') return;
    if (this.filledBy[field] === 'voice') {
      this.analytics.capture('create_event_voice_field_corrected', { field, surface: this.surface });
    }
    this.filledBy = { ...this.filledBy, [field]: 'user' };
    if (this.voiceSnapshot) this.voiceCollapsed = true;
  }

  onVoiceParsed(p: VoiceParsed): void {
    this.voiceSnapshot = { draft: this.snapshot(), filledBy: { ...this.filledBy } };
    const patch = this.applyParse(p.result);

    this.voiceNotes = Array.from(new Set(patch.notes));
    this.voicePartial = this.partialMessage(p);
    this.voiceCollapsed = false;
    this.usedVoice = true;

    this.analytics.capture('create_event_voice_parsed', {
      surface: this.surface,
      input: p.input,
      parser: 'local',
      fields_filled: patch.filled,
      fields_count: patch.filled.length,
      kept_yours: patch.keptYours,
      low_confidence: patch.lowConfidence,
      hinglish_hit: p.result.hinglish,
      parse_ms: p.parseMs,
    });
  }

  /**
   * Writes a parse into the form under the voice-merge rules — a field the
   * host set by hand is never overwritten — and marks what it filled as
   * 'voice'. Shared by "Say it instead" and the conversation.
   */
  private applyParse(result: ParseResult): VoicePatch {
    const patch = mergeParse(result, this.filledBy);

    const values: Record<string, string> = {};
    if (patch.eventName !== undefined) values['eventName'] = patch.eventName;
    if (patch.eventCategory !== undefined) values['eventCategory'] = patch.eventCategory;
    if (patch.hostedBy !== undefined) values['hostedBy'] = patch.hostedBy;
    if (patch.startDate !== undefined) values['startDate'] = patch.startDate;
    if (patch.startTime !== undefined) values['startTime'] = patch.startTime;
    // emitEvent: false keeps these from reading as the host's own edits.
    this.form.patchValue(values, { emitEvent: false });
    if (patch.eventCategory !== undefined) this.showCustomCategory = !isPopularCategory(patch.eventCategory);
    if (patch.durationHours !== undefined) {
      this.durationHours = patch.durationHours;
      this.showCustomEnd = false;
    }
    this.applyDuration();
    this.form.updateValueAndValidity({ emitEvent: false });

    const next = { ...this.filledBy };
    for (const f of patch.filled) next[f] = 'voice';
    this.filledBy = next;
    return patch;
  }

  /**
   * Takes back what voice filled in — and only that. A field the host has
   * edited since keeps their answer; a field voice never touched is left alone.
   */
  onVoiceUndo(): void {
    if (!this.voiceSnapshot) return;
    const { draft, filledBy: before } = this.voiceSnapshot;
    const undo = (f: FieldKey) => this.filledBy[f] === 'voice';
    const next = { ...this.filledBy };

    const values: Record<string, string> = {};
    if (undo('eventName')) values['eventName'] = draft.eventName;
    if (undo('hostedBy')) values['hostedBy'] = draft.hostedBy;
    if (undo('eventCategory')) {
      values['eventCategory'] = draft.eventCategory;
      this.showCustomCategory = draft.showCustomCategory;
    }
    if (undo('start')) {
      values['startDate'] = draft.startDate;
      values['startTime'] = draft.startTime;
    }
    if (undo('duration')) {
      values['endDate'] = draft.endDate;
      values['endTime'] = draft.endTime;
      this.durationHours = draft.durationHours;
      this.showCustomEnd = draft.durationHours === null;
    }
    this.form.patchValue(values, { emitEvent: false });
    // A preset end follows whichever start is now in place.
    this.applyDuration();
    for (const f of Object.keys(next) as FieldKey[]) if (undo(f)) next[f] = before[f];
    this.filledBy = next;
    // Back to how it was: empty fields shouldn't suddenly show errors.
    for (const name of Object.keys(values)) this.form.get(name)?.markAsUntouched();
    this.form.updateValueAndValidity({ emitEvent: false });
    this.voiceSnapshot = null;
    this.voiceNotes = [];
    this.voicePartial = '';
    this.voiceCollapsed = false;
    this.analytics.capture('create_event_voice_undone', { surface: this.surface });
  }

  /** "We couldn't catch the start time. Pick it below." — only for what is still unanswered. */
  private partialMessage(p: VoiceParsed): string {
    const v = this.form.value as Record<string, any>;
    const missed = missedFields(p.result).filter(f => {
      if (this.filledBy[f] === 'user') return false;
      if (f === 'start') return true;
      return !(v[f] || '').toString().trim();
    });
    if (!missed.length) return '';
    const names = missed.map(f => FIELD_NAMES[f]);
    const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
    return missed.length === 1 && missed[0] === 'start'
      ? `We couldn't catch ${list}. Pick it below.`
      : `We couldn't catch ${list}. Fill ${missed.length === 1 ? 'it' : 'them'} in below.`;
  }

  // ── "Plan it with me" (ChatHost) ──────────────────────────────────────

  /** The tile's tap. Synchronous all the way to the first spoken line (iOS). */
  startChat(): void {
    this.voiceChat?.begin();
  }

  get chatAccountName(): string {
    return (this.initialHostedBy || '').trim();
  }

  chatState(): ChatFormState {
    const v = this.form.value;
    return {
      category: (v.eventCategory || '').trim(),
      name: (v.eventName || '').trim(),
      hasCover: !!this.coverImage,
      start: slotDateTime(v.startDate || '', v.startTime || ''),
      end: slotDateTime(v.endDate || '', v.endTime || ''),
      startIsNow: `${v.startDate} ${v.startTime}` === this.seededStart,
      filledBy: { ...this.filledBy },
    };
  }

  applyChatAnswer(result: ParseResult, _text: string, input: 'voice' | 'chip' | 'typed'): void {
    const patch = this.applyParse(result);
    this.usedChat = true;
    this.flash(patch.filled);
    // The words stay out of analytics; which fields, and how, is enough.
    this.analytics.capture('create_event_chat_filled', {
      surface: this.surface, input, fields_filled: patch.filled, kept_yours: patch.keptYours, low_confidence: patch.lowConfidence,
    });
  }

  setChatCover(dataUrl: string): void {
    this.coverImage = dataUrl;
    this.default_image_id = null;
    this.coverType = 'stock';
    this.usedChat = true;
    this.flash(['cover']);
    this.analytics.capture('create_event_cover_added', { type: 'stock', source: 'chat', surface: this.surface });
  }

  openChatSheet(sheet: 'start' | 'duration' | 'cover'): Promise<void> {
    if (sheet === 'cover') return this.openCoverPicker();
    this.resolveSheetWaiter();
    return new Promise(resolve => {
      this.sheetWaiter = resolve;
      this.openSheetFor(sheet);
    });
  }

  chatAsking(step: ChatStep | null): void {
    this.chatStep = step;
    const target = step ? CreateEventFormComponent.CHAT_TARGET[step] : null;
    if (!target) return;
    // After this change renders: bring the field being asked about into view.
    // 'start', not 'center': the panel docks over the lower half of the form.
    setTimeout(() => {
      const el = this.el.nativeElement.querySelector(`[data-chat="${target}"]`);
      el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 60);
  }

  chatActiveChange(active: boolean): void {
    this.chatActive = active;
    if (!active) this.chatStep = null;
  }

  submitFromChat(): void {
    this.submit();
  }

  /** Which field each question is about, by its data-chat attribute. */
  private static readonly CHAT_TARGET: Record<ChatStep, string | null> = {
    what: 'category', kind: 'category', name: 'name', when: 'start', long: 'duration', host: 'host', cover: 'cover', recap: null,
  };

  isChatAsking(target: string): boolean {
    return !!this.chatStep && CreateEventFormComponent.CHAT_TARGET[this.chatStep] === target;
  }

  /** A short highlight on fields the conversation just filled. */
  private flash(fields: string[]): void {
    const map: Record<string, string> = { eventName: 'name', eventCategory: 'category', hostedBy: 'host', start: 'start', duration: 'duration', cover: 'cover' };
    this.justFilled = new Set(fields.map(f => map[f] || f));
    clearTimeout(this.justFilledTimer);
    this.justFilledTimer = setTimeout(() => (this.justFilled = new Set()), 1800);
  }

  // ── cover ─────────────────────────────────────────────────────────────

  /**
   * The shared picker (CoverImageComponent) — the same stock covers everywhere.
   * Resolves once it has closed, picked or not (the conversation waits on it).
   */
  openCoverPicker(): Promise<void> {
    this.analytics.capture('create_event_cover_picker_opened', { surface: this.surface });
    // The picker jumps to the edit-event page after saving when this flag is
    // set — its own flow, not this one. Set it aside while it's open.
    const editEvent = localStorage.getItem('editEvent');
    if (editEvent !== null) localStorage.removeItem('editEvent');

    return new Promise(resolve => this.dialog.open(CoverImageComponent, {
      width: '500px',
      backdropClass: 'secondary-backdrop',
      panelClass: 'cover-dialog',
      hasBackdrop: true,
      data: { eventName: this.form.value.eventName },
    }).afterClosed().subscribe(() => {
      if (editEvent !== null) localStorage.setItem('editEvent', editEvent);
      const uploaded = localStorage.getItem('cover');
      const defaultUrl = localStorage.getItem('default_image_url');
      if (uploaded) {
        this.coverImage = uploaded;
        this.coverType = 'uploaded';
        this.analytics.capture('create_event_cover_added', { type: 'uploaded', surface: this.surface });
      } else if (defaultUrl) {
        this.coverImage = defaultUrl;
        this.default_image_id = localStorage.getItem('default_image');
        this.coverType = 'stock';
        this.analytics.capture('create_event_cover_added', { type: 'stock', surface: this.surface });
      } else {
        this.analytics.capture('create_event_cover_dismissed', { surface: this.surface });
      }
      resolve();
    }));
  }

  removeCover(): void {
    this.coverImage = null;
    this.default_image_id = null;
    this.coverType = null;
    localStorage.removeItem('cover');
    localStorage.removeItem('default_image');
    localStorage.removeItem('default_image_url');
    this.analytics.capture('create_event_cover_removed', { surface: this.surface });
  }

  // ── create ────────────────────────────────────────────────────────────

  /** The Create button. */
  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isWorking) return;
    this.errorMessage = '';
    this.liveEventConflict = false;

    // Before sign-in and before creating: a host about to make the event they
    // made a few minutes ago almost always meant to edit it.
    const dup = await this.recentDuplicate();
    if (dup && dup.eventId !== this.duplicateDismissedFor) {
      this.duplicateOf = dup;
      this.analytics.capture('create_event_duplicate_prompted', {
        surface: this.surface,
        existing_event_id: dup.eventId,
        minutes_since: Math.round((Date.now() - dup.at) / 60000),
      });
      // After it renders: the prompt may be below the fold, or under the
      // conversation panel when that is what pressed Create.
      setTimeout(() => this.el.nativeElement.querySelector('.cf-dup')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60);
      return;
    }

    if (this.beforeCreate && !(await this.beforeCreate())) return;
    await this.create();
  }

  private async recentDuplicate(): Promise<RecentCreate | null> {
    let uid = '';
    try {
      uid = (await firstValueFrom(this.afAuth.authState))?.uid || '';
    } catch {
      return null;
    }
    const v = this.form.getRawValue();
    return findRecentDuplicate(uid, v.eventName || '', v.startDate || '', v.startTime || '');
  }

  /** "on Sat 24 Oct" for the prompt's title. */
  get duplicateDate(): string {
    const d = this.duplicateOf;
    const start = d ? slotDateTime(d.startDate, d.startTime) : null;
    return start ? start.toFormat('ccc d LLL') : '';
  }

  get duplicateAgo(): string {
    const minutes = this.duplicateOf ? Math.round((Date.now() - this.duplicateOf.at) / 60000) : 0;
    if (minutes < 1) return 'a moment ago';
    return minutes === 1 ? 'a minute ago' : `${minutes} minutes ago`;
  }

  editDuplicateInstead(): void {
    const dup = this.duplicateOf;
    if (!dup) return;
    this.analytics.capture('create_event_duplicate_resolved', { surface: this.surface, choice: 'edit_existing' });
    this.duplicateOf = null;
    this.editExisting.emit(dup.viewerHex);
  }

  createDuplicateAnyway(): void {
    const dup = this.duplicateOf;
    if (!dup) return;
    this.analytics.capture('create_event_duplicate_resolved', { surface: this.surface, choice: 'create_new' });
    this.duplicateDismissedFor = dup.eventId;
    this.duplicateOf = null;
    this.submit();
  }

  /**
   * Creates the event. Public so a surface that paused in beforeCreate (the
   * new-user page's sign-in) can finish the job once it's ready.
   */
  async create(): Promise<void> {
    if (this.isWorking) return;
    this.setWorking(true);
    this.errorMessage = '';
    this.liveEventConflict = false;

    const v = this.form.getRawValue();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const startTime24 = slotTo24h(v.startTime || '');
    const endTime24 = slotTo24h(v.endTime || '');
    if (!startTime24 || !endTime24) {
      this.setWorking(false);
      this.errorMessage = 'Please pick a start and end time.';
      this.analytics.capture('create_event_failed', { reason: 'invalid_time', surface: this.surface });
      return;
    }

    // Interpret the local wall-clock values in the event timezone, then hand
    // the API epoch seconds.
    const start = DateTime.fromISO(`${v.startDate}T${startTime24}`, { zone: timezone }).toJSDate();
    const end = DateTime.fromISO(`${v.endDate}T${endTime24}`, { zone: timezone }).toJSDate();

    // An uploaded cover travels as raw base64; a stock cover travels as an id.
    const cover = this.coverImage?.startsWith('data') ? this.coverImage.split(',')[1] : null;

    localStorage.removeItem('default_image');
    localStorage.removeItem('default_image_url');
    localStorage.removeItem('cover');

    (await this.eventService.createEvent(
      v.eventName,
      v.eventCategory,
      v.hostedBy,
      start.getTime() / 1000,
      end.getTime() / 1000,
      end.getTime() / 1000,
      start.getTime() / 1000,
      '',                   // location — editable later in edit-event
      this.default_image_id,
      '',                   // invite message — set later
      null,
      false,                // rsvp — set later
      cover,
      JSON.stringify([]),   // albums — added later
      this.whatsappUpload.inviteLinkEnabled,
    )).subscribe({
      next: (res: any) => {
        if (res?.['result'] !== 'success') {
          this.setWorking(false);
          // A rejection comes back in `error`; `result` is only ever the
          // literal 'success' or absent/false.
          const backendError = res?.['error'];
          this.liveEventConflict = typeof backendError === 'string' && /live event/i.test(backendError);
          if (this.liveEventConflict) {
            this.errorMessage = 'This account already has a live event.';
          } else {
            const detail = backendError ?? res?.['result'];
            this.errorMessage = typeof detail === 'string' && detail.trim().length > 0
              ? detail
              : "We couldn't create your event just now. Your details are still here, so tap Create event again in a moment.";
          }
          this.analytics.capture('create_event_failed', {
            reason: this.liveEventConflict ? 'live_event_conflict' : 'api_error',
            api: 'pwa_create_event',
            detail: typeof backendError === 'string' ? backendError.slice(0, 120) : undefined,
            surface: this.surface,
          });
          return;
        }
        this.onCreated(res, start, end);
      },
      error: () => {
        this.setWorking(false);
        // The request never got an answer — almost always the connection.
        this.errorMessage = "We couldn't reach Picaggo to create your event. Check your connection, then tap Create event again.";
        this.analytics.capture('create_event_failed', { reason: 'network_error', api: 'pwa_create_event', surface: this.surface });
      },
    });
  }

  /** The live-event conflict's primary action: the existing event is one tap away. */
  goToExistingEvents(): void {
    this.analytics.capture('create_event_conflict_resolved', { surface: this.surface, choice: 'go_to_events' });
    this.router.navigateByUrl('/home');
  }

  useAnotherAccount(): void {
    this.analytics.capture('create_event_conflict_resolved', { surface: this.surface, choice: 'switch_account' });
    this.errorMessage = '';
    this.liveEventConflict = false;
    this.switchAccount.emit();
  }

  /** The analytics and localStorage contract every downstream screen reads, then hand back to the surface. */
  private onCreated(response: any, start: Date, end: Date): void {
    const v = this.form.getRawValue();
    this.analytics.setEventContext(response['event_id'], 'host', response['viewer_hex']);
    this.analytics.capture('event_created', {
      event_id: response['event_id'],
      event_category: v.eventCategory || undefined,
      surface: this.surface,
      ...this.answersSummary(),
      ...this.guestHostPrompt.takeCreatedSource(),
    });
    this.adsConversion.track('event_created', { eventId: response['event_id'] });

    const nowSecs = Date.now() / 1000;
    const startSecs = start.getTime() / 1000;
    const endSecs = end.getTime() / 1000;

    localStorage.setItem('start_time', startSecs.toString());
    localStorage.removeItem('formData');
    if (startSecs > nowSecs) {
      localStorage.setItem('isScheduled', 'true');
    } else if (nowSecs > startSecs && nowSecs < endSecs) {
      localStorage.setItem('isLive', 'true');
    }

    // InviteDialogComponent calls `eventStatus.toLowerCase()` with no null
    // check; getEventQR normally supplies it but can fail, so seed it from the
    // times we already have.
    localStorage.setItem('eventStatus', startSecs > nowSecs ? 'scheduled' : nowSecs < endSecs ? 'live' : 'completed');
    localStorage.setItem('name', v.eventName || '');

    if (response['qrCode']) {
      this.analytics.capture('qr_generated', { event_id: response['event_id'] });
    }

    localStorage.setItem('u', response['user_id']);
    localStorage.setItem('e', response['event_id']);
    localStorage.setItem('qrImage', response['qrCode']);
    // Downstream screens key off the viewer hex, not the numeric id.
    localStorage.setItem('eventId', response['viewer_hex']);
    localStorage.setItem('viewer_qrImage', response['viewer_qrCode']);
    localStorage.setItem('web_qrImage', response['web_qrCode']);
    localStorage.setItem('web_viewer_qrImage', response['web_viewer_qrCode']);
    localStorage.setItem('isHost', 'true');

    // `invite_code` is the event's `hash` column, the same code getEventQR
    // hands back for a live or scheduled event. Storing it lets the invite
    // screen show the link the moment it opens instead of waiting on that
    // round-trip. Past events too: getEventQR gives those a separate expiring
    // code, but i.picaggo.com resolves the event hash for any event
    // (sp_fetch_hex_data), so the host isn't left on "Preparing your link…".
    if (response['invite_code']) {
      localStorage.setItem('event_hash', response['invite_code']);
      localStorage.setItem(INVITE_HASH_EVENT_KEY, String(response['event_id']));
      if (response['viewer_hex']) localStorage.setItem('viewer_hash', response['viewer_hex']);
    }
    // Both codes, per event — what the invite screen builds both links from.
    this.inviteCodes.remember(response['event_id'], response['invite_code'], response['viewer_hex']);
    // Create mints the WhatsApp invite link too (whatsapp_invite). If it
    // didn't come back — an older Lambda, or minting failed — start the
    // separate request now; the invite screen joins it instead of its own.
    if (!this.whatsappUpload.seedInviteLink(response['event_id'], response['whatsapp_invite'])) {
      this.whatsappUpload.prefetchInviteLink(response['event_id'], v.eventName || '');
    }

    // The new event must show up in home's list; both entries are cached per session.
    this.apiCache.clearCache('pwa_get_home_page_data');
    this.apiCache.clearCache('pwa_get_total_categories');

    // So the next Create within half an hour can ask "edit this one instead?".
    this.afAuth.currentUser.then(user => {
      if (!user?.uid || !response['viewer_hex']) return;
      rememberCreate({
        uid: user.uid,
        eventId: String(response['event_id']),
        viewerHex: response['viewer_hex'],
        name: v.eventName || '',
        startDate: v.startDate || '',
        startTime: v.startTime || '',
        at: Date.now(),
      });
    }).catch(() => { /* no account to key it by: nothing to remember */ });

    this.setWorking(false);
    this.created.emit({
      eventId: response['event_id'],
      viewerHex: response['viewer_hex'],
      eventName: v.eventName || '',
      category: v.eventCategory || '',
      hostedBy: v.hostedBy || '',
      endSummary: this.endSummary,
      coverImage: this.coverImage,
      start,
      end,
    });
  }

  /**
   * How the host answered, without the answers themselves: whether they kept
   * the defaults (4 hours, starts now), added a cover, and so on. Sent with
   * event_created, and by a surface that is abandoned part-way
   * (create_event_abandoned) to show how far the host got.
   */
  answersSummary(): Record<string, any> {
    const v = this.form.getRawValue();
    return {
      duration_hours: this.durationHours ?? 'custom',
      kept_default_duration: this.durationHours === CreateEventFormComponent.DEFAULT_DURATION_HOURS,
      starts_later: `${v.startDate} ${v.startTime}` !== this.seededStart,
      has_cover: !!this.coverImage,
      cover_type: this.coverImage ? (this.coverType || 'uploaded') : null,
      category_filled: !!v.eventCategory,
      category_is_custom: this.showCustomCategory,
      name_filled: !!(v.eventName || '').trim(),
      hosted_by_filled: !!(v.hostedBy || '').trim(),
      layout: this.rows ? 'rows' : 'classic',
      used_voice: this.usedVoice,
      used_chat: this.usedChat,
      voice_fields_kept: Object.values(this.filledBy).filter(o => o === 'voice').length,
    };
  }

  private setWorking(working: boolean): void {
    this.isWorking = working;
    this.workingChange.emit(working);
  }
}
